/**
 * Heyoravix Sticky Add to Cart
 * Self-contained Web Component. No external libraries, no jQuery.
 *
 * Variant sync strategy (see chat explanation for why):
 * - We embed the full product's variants (id, option1/2/3, price, compare_at_price,
 *   available, featured image, quantity_rule) as JSON directly in this section.
 * - We never assume the theme's internal custom-element event names. Instead we:
 *     a) listen for native 'change' events bubbling from the main variant picker's
 *        radio/select inputs (these always fire regardless of theme internals),
 *        read the checked/selected values, and match them against our own
 *        variants JSON to resolve the resulting variant ourselves.
 *     b) when our own sticky dropdown changes, we update the corresponding
 *        radio/select inputs in the main picker and dispatch a native 'change'
 *        event on them so the theme's own JS reacts exactly as if the shopper
 *        had clicked there.
 * - Add to cart goes through the standard /cart/add.js Ajax endpoint (always
 *   compatible). After a successful add we call cart-drawer-component's public
 *   open() method if present, which handles its own content refresh natively.
 *   We update the cart bubble count locally (optimistic) instead of an extra
 *   network round trip, to keep this bar from adding request volume on top of
 *   whatever the main add-to-cart flow already does.
 */

(function () {
  if (customElements.get('sticky-atc-heyoravix')) return;

  class HeyoravixStickyATC extends HTMLElement {
    constructor() {
      super();
      this._onMainVariantChange = this._onMainVariantChange.bind(this);
      this._onMainQuantityChange = this._onMainQuantityChange.bind(this);
      this._onIntersect = this._onIntersect.bind(this);
      this._syncingFromMain = false;
      this._syncingFromSticky = false;
      this._syncingQtyFromMain = false;
      this._syncingQtyFromSticky = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.trigger = this.dataset.trigger || 'after_form';
      this.scrollDistance = parseInt(this.dataset.scrollDistance || '600', 10);
      this.successBehavior = this.dataset.successBehavior || 'open_drawer';
      this.hideVariantIfSingle = this.dataset.hideVariantIfSingle === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        this.variants = JSON.parse(this.querySelector('[data-hv-variants]').textContent || '[]');
      } catch (e) {
        this.variants = [];
      }
      try {
        const rawInitial = JSON.parse(this.querySelector('[data-hv-initial-variant]').textContent || 'null');
        this.initialVariant =
          (rawInitial && this._findVariantByIdIn(this.variants, rawInitial.id)) || this.variants[0] || rawInitial;
      } catch (e) {
        this.initialVariant = this.variants[0] || null;
      }

      this.currentVariant = this.initialVariant;

      this.$form = this.querySelector('[data-hv-form]');
      this.$image = this.querySelector('[data-hv-image]');
      this.$imageWrap = this.querySelector('[data-hv-image-wrap]');
      this.$title = this.querySelector('[data-hv-title]');
      this.$price = this.querySelector('[data-hv-price]');
      this.$comparePrice = this.querySelector('[data-hv-compare-price]');
      this.$variantWrap = this.querySelector('[data-hv-variant-wrap]');
      this.$variantSelect = this.querySelector('[data-hv-variant-select]');
      this.$qtyWrap = this.querySelector('[data-hv-qty-wrap]');
      this.$qtyInput = this.querySelector('[data-hv-qty-input]');
      this.$qtyMinus = this.querySelector('[data-hv-qty-minus]');
      this.$qtyPlus = this.querySelector('[data-hv-qty-plus]');
      this.$button = this.querySelector('[data-hv-button]');
      this.$buttonLabel = this.querySelector('[data-hv-button-label]');
      this.$error = this.querySelector('[data-hv-error]');
      this.$status = this.querySelector('[data-hv-status]');
      this.$collapseToggle = this.querySelector('[data-hv-collapse-toggle]');

      this._labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        adding: this.dataset.labelAdding || 'Adding…',
        added: this.dataset.labelAdded || 'Added',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
      };

      this._bindQuantity();
      this._bindVariantSelect();
      this._bindAddToCart();
      this._bindMainVariantPickerSync();
      this._bindMainQuantitySync();
      this._bindCollapseToggle();
      this._setupVisibility();

      this._renderVariant(this.currentVariant, { silent: true });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      document.removeEventListener('change', this._onMainVariantChange);
      document.removeEventListener('change', this._onMainQuantityChange);
      document.removeEventListener('input', this._onMainQuantityChange);
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
    }

    _bindCollapseToggle() {
      if (!this.$collapseToggle) return;
      this.$collapseToggle.addEventListener('click', () => {
        const collapsed = this.classList.toggle('is-collapsed');
        this.$collapseToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      });
    }

    /* ---------------- Visibility ---------------- */

    _setupVisibility() {
      if (this.trigger === 'always') {
        this._show();
        return;
      }

      if (this.trigger === 'after_scroll') {
        this._scrollHandler = () => {
          if (window.scrollY > this.scrollDistance) {
            this._show();
          } else {
            this._hide();
          }
        };
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
        this._scrollHandler();
        return;
      }

      const target =
        document.querySelector('[data-hv-observe-target]') ||
        document.querySelector('product-form-component form') ||
        document.querySelector('.product-form') ||
        document.querySelector('main [type="submit"][name="add"]');

      if (!target) {
        this._scrollHandler = () => {
          if (window.scrollY > this.scrollDistance) {
            this._show();
          } else {
            this._hide();
          }
        };
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
        this._scrollHandler();
        return;
      }

      this._observer = new IntersectionObserver(this._onIntersect, {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      });
      this._observer.observe(target);

      if (window.Shopify && window.Shopify.designMode) {
        this._show();
      }
    }

    _onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        this._hide();
      } else if (entry.boundingClientRect.top < 0) {
        this._show();
      }
    }

    _show() {
      this.classList.add('is-visible');
      this.setAttribute('aria-hidden', 'false');
    }

    _hide() {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
    }

    /* ---------------- Variant rendering ---------------- */

    _findVariantByIdIn(list, id) {
      return (list || []).find((v) => String(v.id) === String(id)) || null;
    }

    _findVariantById(id) {
      return this._findVariantByIdIn(this.variants, id);
    }

    _findVariantByOptions(options) {
      return (
        this.variants.find(
          (v) =>
            (v.option1 || null) === (options[0] || null) &&
            (v.option2 || null) === (options[1] || null) &&
            (v.option3 || null) === (options[2] || null)
        ) || null
      );
    }

    _renderVariant(variant, opts) {
      opts = opts || {};
      if (!variant) return;
      this.currentVariant = variant;

      if (this.$form) {
        const idInput = this.$form.querySelector('[name="id"]');
        if (idInput) idInput.value = variant.id;
      }

      if (this.$price) this.$price.innerHTML = variant.price_formatted || '';
      if (this.$comparePrice) {
        if (variant.compare_at_price_formatted && variant.compare_at_price > variant.price) {
          this.$comparePrice.innerHTML = variant.compare_at_price_formatted;
          this.$comparePrice.hidden = false;
        } else {
          this.$comparePrice.hidden = true;
        }
      }

      if (this.$image && variant.featured_image) {
        this.$image.src = variant.featured_image;
        this.$image.srcset = '';
      }

      if (this.$variantSelect && !opts.silent && this.$variantSelect.value !== String(variant.id)) {
        this.$variantSelect.value = variant.id;
      }

      this._updateQuantityLimits(variant);
      this._updateButtonAvailability(variant);

      if (!opts.silent && !this._syncingFromMain) {
        this._syncMainPicker(variant);
      }
    }

    _updateButtonAvailability(variant) {
      if (!this.$button) return;
      if (!variant) {
        this.$button.disabled = true;
        this._setButtonLabel(this._labels.unavailable);
        return;
      }
      if (!variant.available) {
        this.$button.disabled = true;
        this._setButtonLabel(this._labels.soldOut);
      } else if (this.$button.dataset.state !== 'adding') {
        this.$button.disabled = false;
        this._setButtonLabel(this._labels.add);
        this.$button.removeAttribute('data-state');
      }
    }

    _setButtonLabel(text) {
      if (this.$buttonLabel) this.$buttonLabel.textContent = text;
    }

    /* ---------------- Quantity ---------------- */

    _bindQuantity() {
      if (!this.$qtyInput) return;

      this.$qtyMinus?.addEventListener('click', () => this._stepQuantity(-1));
      this.$qtyPlus?.addEventListener('click', () => this._stepQuantity(1));
      this.$qtyInput.addEventListener('change', () => this._clampQuantity());
    }

    _getQty() {
      return parseInt(this.$qtyInput.textContent, 10);
    }

    _setQty(value) {
      this.$qtyInput.textContent = value;
    }

    _updateQuantityLimits(variant) {
      if (!this.$qtyInput) return;
      const rule = (variant && variant.quantity_rule) || { min: 1, max: null, increment: 1 };
      this.$qtyInput.dataset.min = rule.min || 1;
      this.$qtyInput.dataset.max = rule.max || '';
      this.$qtyInput.dataset.step = rule.increment || 1;

      const min = parseInt(this.$qtyInput.dataset.min, 10) || 1;
      if (this._getQty() < min || isNaN(this._getQty())) {
        this._setQty(min);
      }
      this._clampQuantity();
    }

    _clampQuantity() {
      const min = parseInt(this.$qtyInput.dataset.min || '1', 10);
      const max = this.$qtyInput.dataset.max ? parseInt(this.$qtyInput.dataset.max, 10) : null;
      let value = this._getQty();
      if (isNaN(value) || value < min) value = min;
      if (max && value > max) value = max;
      this._setQty(value);

      if (this.$qtyMinus) this.$qtyMinus.disabled = value <= min;
      if (this.$qtyPlus) this.$qtyPlus.disabled = !!max && value >= max;

      if (!this._syncingQtyFromMain) {
        this._syncMainQuantity(value);
      }
    }

    _stepQuantity(direction) {
      const step = parseInt(this.$qtyInput.dataset.step || '1', 10) || 1;
      const current = this._getQty() || 1;
      this._setQty(current + direction * step);
      this._clampQuantity();
    }

    _getMainQuantityInput() {
      return (
        document.querySelector('product-form-component input[name="quantity"]') ||
        document.querySelector('main input[name="quantity"]') ||
        document.querySelector('input[name="quantity"]')
      );
    }

    _syncMainQuantity(value) {
      const mainInput = this._getMainQuantityInput();
      if (!mainInput || this.contains(mainInput)) return;
      if (parseInt(mainInput.value, 10) === value) return;

      this._syncingQtyFromSticky = true;
      mainInput.value = value;
      mainInput.dispatchEvent(new Event('input', { bubbles: true }));
      mainInput.dispatchEvent(new Event('change', { bubbles: true }));
      this._syncingQtyFromSticky = false;
    }

    _bindMainQuantitySync() {
      document.addEventListener('change', this._onMainQuantityChange);
      document.addEventListener('input', this._onMainQuantityChange);
    }

    _onMainQuantityChange(event) {
      if (this._syncingQtyFromSticky) return;
      const target = event.target;
      if (!target || target.getAttribute('name') !== 'quantity') return;
      if (this.contains(target)) return;

      const value = parseInt(target.value, 10);
      if (isNaN(value) || !this.$qtyInput) return;

      this._syncingQtyFromMain = true;
      this._setQty(value);
      this._clampQuantity();
      this._syncingQtyFromMain = false;
    }

    /* ---------------- Sticky variant select ---------------- */

    _bindVariantSelect() {
      if (!this.$variantSelect) return;

      if (this.hideVariantIfSingle && this.variants.length <= 1 && this.$variantWrap) {
        this.$variantWrap.hidden = true;
      }

      this.$variantSelect.addEventListener('change', () => {
        const variant = this._findVariantById(this.$variantSelect.value);
        if (variant) this._renderVariant(variant);
      });
    }

    _syncMainPicker(variant) {
      const mainPicker = document.querySelector('variant-picker[ref="mainVariantPicker"]') || document.querySelector('variant-picker');
      if (!mainPicker) return;

      this._syncingFromSticky = true;

      const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      const fieldsets = mainPicker.querySelectorAll('.variant-option[data-fieldset-index]');
      fieldsets.forEach((fieldset, index) => {
        const desiredValue = options[index];
        if (desiredValue == null) return;
        const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
          (el) => el.value === desiredValue
        );
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      const selects = mainPicker.querySelectorAll('select.variant-option__select');
      selects.forEach((select, index) => {
        const desiredValue = options[index];
        if (desiredValue == null) return;
        if (select.value !== desiredValue) {
          select.value = desiredValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      this._syncingFromSticky = false;
    }

    _bindMainVariantPickerSync() {
      document.addEventListener('change', this._onMainVariantChange);
    }

    _onMainVariantChange(event) {
      if (this._syncingFromSticky) return;
      const target = event.target;
      if (!target.closest('.variant-picker__form')) return;

      const picker = target.closest('variant-picker');
      if (!picker) return;

      const selected = [];
      const fieldsets = picker.querySelectorAll('.variant-option[data-fieldset-index]');
      if (fieldsets.length) {
        fieldsets.forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) selected.push(checked.value);
        });
      } else {
        picker.querySelectorAll('select.variant-option__select').forEach((select) => {
          selected.push(select.value);
        });
      }

      if (!selected.length) return;

      const variant = this._findVariantByOptions(selected);
      if (variant) {
        this._syncingFromMain = true;
        this._renderVariant(variant);
        this._syncingFromMain = false;
      }
    }

    /* ---------------- Add to cart ---------------- */

    _bindAddToCart() {
      if (!this.$button) return;
      this.$button.addEventListener('click', (event) => {
        event.preventDefault();
        this._addToCart();
      });
    }

    _collectProperties() {
      const properties = {};
      const mainForm =
        document.querySelector('product-form-component form') || document.querySelector('.product-form');
      if (!mainForm) return properties;
      mainForm.querySelectorAll('[name^="properties["]').forEach((el) => {
        const match = el.name.match(/properties\[(.+)\]/);
        if (match) properties[match[1]] = el.value;
      });
      return properties;
    }

    async _addToCart() {
      if (!this.currentVariant || !this.currentVariant.available) return;
      if (this.$button.dataset.state === 'adding') return;

      this.$button.dataset.state = 'adding';
      this.$button.disabled = true;
      this._setButtonLabel(this._labels.adding);
      this._hideError();
      this._announce(this._labels.adding);

      const properties = this._collectProperties();
      const requestedQuantity = (this.$qtyInput ? this._getQty() : 1) || 1;
      const body = {
        id: this.currentVariant.id,
        quantity: requestedQuantity,
      };
      if (Object.keys(properties).length) body.properties = properties;
      if (this.currentVariant.selling_plan_allocations && this.currentVariant.selling_plan_allocations.length) {
        const sellingPlanInput = document.querySelector('[name="selling_plan"]:checked, select[name="selling_plan"]');
        if (sellingPlanInput && sellingPlanInput.value) {
          body.selling_plan = sellingPlanInput.value;
        }
      }

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Unable to add this item to your cart.');
        }

        this.$button.dataset.state = 'added';
        this._setButtonLabel(this._labels.added);
        this._announce(this._labels.added);
        this._refreshCartUI(requestedQuantity);

        setTimeout(() => {
          if (this.$button.dataset.state === 'added') {
            this.$button.removeAttribute('data-state');
            this.$button.disabled = false;
            this._setButtonLabel(this._labels.add);
          }
        }, 2000);

        if (this.successBehavior === 'redirect') {
          window.location.href = `${root}cart`;
        } else if (this.successBehavior === 'message') {
          // Label + aria-live message already communicated the success; nothing further to do.
        }
      } catch (error) {
        this.$button.removeAttribute('data-state');
        this.$button.disabled = false;
        this._setButtonLabel(this._labels.add);
        this._showError(error.message || 'Something went wrong. Please try again.');
        this._announce(error.message || 'Something went wrong.');
      }
    }

    /**
     * Reflects a successful add-to-cart without any extra network requests.
     * Cart bubble counts are bumped optimistically (client-side math), and
     * cart-drawer-component's own open() call is trusted to fetch/render its
     * own fresh content — it already does this natively, so re-fetching the
     * cart section here ourselves was pure duplicate request volume on top of
     * whatever the main add-to-cart flow already triggers, which is what was
     * pushing this store over Shopify's rate limit during quick successive
     * actions.
     * @param {number} addedQuantity - The quantity that was just added.
     */
    _refreshCartUI(addedQuantity) {
      if (addedQuantity && Number.isFinite(addedQuantity)) {
        document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
          const current = parseInt(el.textContent, 10);
          if (!isNaN(current)) {
            el.textContent = String(current + addedQuantity);
          }
        });
      }

      if (this.successBehavior === 'open_drawer') {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      }
    }

    _showError(message) {
      if (!this.$error) return;
      this.$error.textContent = message;
      this.$error.hidden = false;
    }

    _hideError() {
      if (!this.$error) return;
      this.$error.hidden = true;
    }

    _announce(message) {
      if (this.$status) this.$status.textContent = message;
    }
  }

  customElements.define('sticky-atc-heyoravix', HeyoravixStickyATC);
})();